import React from 'react';
import { MessageBubble } from '../MessageBubble';
import { Sparkles } from 'lucide-react';
import { Trash2 } from 'lucide-react';
import { ClarificationPayload } from '../../types/chat';

interface Message {
  id: string;
  content: string | null | undefined;
  role: string;
  message_type: string;
  created_at: string | Date;
  status?: 'sending' | 'sent' | 'failed';
  media_path?: string;
  media_id?: string;
  voice_duration?: number;
  tempAudioUrl?: string;
  tempMediaUrl?: string;
  tempFileUrl?: string;
  fileName?: string;
  fileSize?: number;
  fileType?: string;
  clarification?: ClarificationPayload;
  pendingHint?: 'image_processing' | 'attachment_processing';
  file_metadata?: { original_filename: string };
  senderDisplay: string;
}

interface MessageContainerProps {
  messages: Message[];
  selectedSubject: string | null;
  selectedSubjectName?: string | null;
  userFirstName: string;
  isProfileOpen: boolean;
  messagesEndRef: React.RefObject<HTMLDivElement>;
  showActionButtons?: boolean;
  onDeleteMessage?: (messageId: string) => Promise<void>;
  onSelectClarificationOption?: (optionId: string) => void;
  onEnableClarificationOther?: () => void;
}


export const MessageContainer: React.FC<MessageContainerProps> = ({
  messages,
  selectedSubject,
  selectedSubjectName,
  userFirstName,
  isProfileOpen,
  messagesEndRef,
  showActionButtons = true,
  onDeleteMessage,
  onSelectClarificationOption,
  onEnableClarificationOther,
}) => {
  if (messages.length === 0) {
    return (
      <div className="text-center px-4">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-[var(--accent-primary)] to-[var(--accent-secondary)] mb-6 shadow-lg shadow-[var(--accent-primary)]/20 animate-pulse">
          <Sparkles className="w-8 h-8 text-white" />
        </div>
        
        <h1 className="text-4xl sm:text-5xl font-bold mb-4">
          <span className="bg-gradient-to-r from-cyan-400 via-blue-400 to-purple-400 bg-clip-text text-transparent">
            Hi, {userFirstName}
          </span>
        </h1>
      </div>
    );
  }

  // Handle delete message with confirmation
  const handleDelete = async (messageId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (onDeleteMessage && window.confirm('Are you sure you want to delete this message?')) {
      await onDeleteMessage(messageId);
    }
  };

  // Has messages - show them
  return (
    <div className="max-w-3xl mx-auto space-y-4 p-4 sm:p-6">
      {messages.map((message) => (
        <div 
          key={message.id} 
          className="group relative"
        >
          {/* Delete button - appears on hover */}
          {onDeleteMessage && message.status !== 'sending' && (
            <button
              onClick={(e) => handleDelete(message.id, e)}
              className={`
                absolute top-1 z-10
                ${message.role === 'user' ? 'left-0 -translate-x-8' : 'right-0 translate-x-8'}
                opacity-0 group-hover:opacity-100
                p-1.5 rounded-lg
                bg-red-500/10 hover:bg-red-500/20
                border border-red-400/20 hover:border-red-400/40
                text-red-400 hover:text-red-300
                transition-all duration-200
                backdrop-blur-sm
              `}
              title="Delete message"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
          <MessageBubble
            message={message}
            isUser={message.role === 'user'}
            isProfileOpen={isProfileOpen}
            onSelectClarificationOption={onSelectClarificationOption}
            onEnableClarificationOther={onEnableClarificationOther}
          />
        </div>
      ))}
      <div ref={messagesEndRef} className="h-4" />
    </div>
  );
};
